<template>
  <div class="edition-panel">
    <form class="controls my-3">
      <div class="row align-items-end">
        <slot/>
        <div class="col text-center">
          Versos de {{ start }} a {{ end }}.
          <b-input-group class="col">
            <b-input-group-prepend>
              <b-button variant="outline-primary" @click="previousVerses(side)">‹</b-button>
            </b-input-group-prepend>
            <b-form-select v-model="pageSize" :options="[30, 50, 100]" class="col"/>
            <b-input-group-append>
              <b-button variant="outline-primary" @click="nextVerses(side)">›</b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div class="ml-auto">
          <b-form-checkbox v-model="showReferences" button-variant="outline-primary" button>Referencias
          </b-form-checkbox>
          <a :class="{disabled: !link}" class="btn btn-primary" :href="link" target="_blank">
            <i class="fa fa-search"></i>
          </a>
          <button class="btn btn-primary" @click.prevent="align(side)">Alinear</button>
        </div>
      </div>
    </form>
    <div ref="textRoot" class="display">
      <bibliography v-if="showReferences"/>
      <template v-else>
        <cover v-if="start === 1"/>
        <journey v-for="journey in section.elements" :journey="journey"/>
      </template>
    </div>
  </div>
</template>
<script>
  import findIndex from 'lodash/findIndex'
  import findLastIndex from 'lodash/findLastIndex'
  import inRange from 'lodash/inRange'
  import dropWhile from 'lodash/dropWhile'
  import dropRightWhile from 'lodash/dropRightWhile'
  import isNumber from 'lodash/isNumber'
  import isNil from 'lodash/isNil'
  import Journey from './Journey'
  import {mapState, mapMutations} from 'vuex'
  import Cover from './Cover'
  import Bibliography from './Bibliography'

  export default {
    components: {Bibliography, Cover, Journey},
    props: {
      side: {type: String, required: true}
    },
    data() {
      return {link: null, showReferences: false}
    },
    created() {
      window.addEventListener('mouseup', this.buildLink)
    },
    beforeDestroy() {
      window.removeEventListener('mouseup', this.buildLink)
    },
    computed: {
      ...mapState({document: s => s.document}),
      section() {
        return this.partition(this.document.base)
      },
      start() {
        return this.$store.state[this.side].start
      },
      end() {
        return this.$store.state[this.side].end
      },
      pageSize: {
        get() {
          return this.$store.state[this.side].pageSize
        },
        set(pageSize) {
          this.$store.commit('setPageSize', {side: this.side, pageSize})
        }
      }
    },
    methods: {
      ...mapMutations(['nextVerses', 'previousVerses', 'align']),
      // Return an element with only the child elements that should be shown for the current verse range.
      // That is all the child elements that have a verse range that matches the current one applied recursively
      // and including elements without verse (notes, prose) that are directly adjacent to the current verse range.
      partition(element) {
        const {verse: leftVerseIndex, line: leftLineIndex} = this.getFirstIndexes(element.elements, true)
        const {verse: rightVerseIndex, line: rightLineIndex} = this.getFirstIndexes(element.elements, false)
        const slice = element.elements.slice()
        // If the first matching element is not completely inside the range, we have to partition it.
        if (!this.isInside(slice[leftVerseIndex])) {
          slice[leftVerseIndex] = this.partition(slice[leftVerseIndex])
        }
        // Same for the last element.
        if (!this.isInside(slice[rightVerseIndex])) {
          slice[rightVerseIndex] = this.partition(slice[rightVerseIndex])
        }

        return {...element, elements: slice.slice(leftLineIndex, rightLineIndex + 1)}
      },
      getFirstIndexes(elements, ascending) {
        const step = ascending ? -1 : 1
        const verseIndex = this.findIndex(elements, ascending, this.intersect)
        // Search for elements without verse (notes, prose...) before/after the first element that matches the current range
        let lineIndex = verseIndex
        if (ascending && elements[verseIndex].verse_index.start >= this.start ||
            !ascending && elements[verseIndex].verse_index.end <= this.end) {
          while (elements[lineIndex + step] && isNil(elements[lineIndex + step].verse_index)) {
            lineIndex += step
          }
        }
        return {verse: verseIndex, line: lineIndex}
      },
      intersect(element) {
        if (!element.verse_index) {
          return false
        } else if (isNumber(element.verse_index)) {
          return this.inRange(element.verse_index)
        } else {
          return element.verse_index.start <= this.end && element.verse_index.end >= this.start
        }
      },
      isInside(element) {
        if (isNumber(element.verse_index)) {
          return this.inRange(element.verse_index)
        } else {
          return this.inRange(element.verse_index.start) && this.inRange(element.verse_index.end)
        }
      },
      findIndex(elements, ascending, predicate) {
        const find = ascending ? findIndex : findLastIndex
        return find(elements, predicate)
      },
      inRange(number) {
        return inRange(number, this.start, this.end + 1)
      },
      buildLink() {
        if (window.getSelection().toString() !== '') {
          const text = window.getSelection().toString().split(/[ \n\t]/).slice(0, 5).join(' ')
          this.link = `./busquedas?t=${this.document.base.title}&w=${text}`
        } else {
          this.link = null
        }
      }
    }
  }
</script>
<style lang="scss" scoped>
  .controls {
    position: sticky;
    top: 0;
    padding: 0.5rem;
    z-index: 1;
    background-color: white;
  }
</style>